import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  LinearProgress,
  Alert,
  Chip,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  MdSearch,
  MdRefresh,
  MdInfoOutline,
  MdFileDownload,
  MdGridOn,
  MdPrint,
  MdAccountBalanceWallet,
  MdPayments,
  MdHourglassBottom,
  MdErrorOutline,
  MdSyncProblem,
  MdCreditCardOff,
  MdTimer,
  MdWarningAmber,
} from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';
import {
  FONT,
  inr,
  fmtDate,
  toISO,
  authToken,
  PRESETS,
  presetRange,
  rangeLabel,
  StatCard,
  statGridSx,
  exportButtonSx,
  downloadCsv,
  downloadExcel,
  openPrintView,
} from './reportShared';

const REPORT_ROUTE = 'reports/seller-settlement';
const pill = (bg, fg) => ({ fontWeight: 700, fontFamily: FONT, background: bg, color: fg, height: 24, fontSize: 11 });
const label = (s) => (s ? String(s).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '-');
const dash = <Typography component="span" sx={{ color: '#94a3b8' }}>—</Typography>;
const PAYOUT_STATUS = { initiated: ['#fef3c7', '#92400e'], processing: ['#e0f2fe', '#075985'], completed: ['#dcfce7', '#166534'], failed: ['#fee2e2', '#991b1b'] };
const AGE_COLORS = { '0-7': ['#dcfce7', '#166534'], '8-15': ['#fef3c7', '#92400e'], '16-30': ['#ffedd5', '#9a3412'], '30+': ['#fee2e2', '#991b1b'] };
const signed = (v) => <span style={{ color: v < 0 ? '#b91c1c' : undefined }}>{inr(v)}</span>;

const SellerCell = ({ r }) => (
  <Box sx={{ lineHeight: 1.25, minWidth: 160 }}>
    <Typography sx={{ fontSize: 11.5, fontWeight: 700, fontFamily: FONT }}>{r.seller}</Typography>
    <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{[r.sellerCode, r.contact].filter(Boolean).join(' · ') || '—'}</Typography>
  </Box>
);

const col = (key, lbl, opts = {}) => ({ key, label: lbl, ...opts });
const money = (key, lbl, extra = {}) => col(key, lbl, { numeric: true, text: (r) => r[key], cell: (r) => signed(r[key]), print: (r) => inr(r[key]), ...extra });

const TABS = [
  {
    key: 'statement',
    label: 'Seller Statement',
    defaultSort: 'due_desc',
    sorts: [
      ['due_desc', 'Ledger due: high to low'],
      ['age_desc', 'Oldest unpaid first'],
      ['mismatch_desc', 'Largest mismatch first'],
      ['paid_desc', 'Paid in period: high to low'],
      ['name_asc', 'Name: A to Z'],
    ],
    columns: [
      col('seller', 'Seller', { text: (r) => r.seller, cell: (r) => <SellerCell r={r} />, extra: (r) => ({ 'Seller Code': r.sellerCode }) }),
      money('openingBalance', 'Opening'),
      money('earned', 'Earned', { cell: (r) => <span style={{ color: '#15803d', fontWeight: 700 }}>{inr(r.earned)}</span> }),
      money('returns', 'Returns'),
      money('paid', 'Paid Out'),
      money('closingBalance', 'Closing Balance', { bold: true }),
      col('ledgerDue', 'Ledger Due', {
        numeric: true,
        bold: true,
        text: (r) => r.ledgerDue,
        print: (r) => inr(r.ledgerDue),
        extra: (r) => ({ 'Pending Entries': r.pendingEntries }),
        cell: (r) => (
          <Box sx={{ lineHeight: 1.25 }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: r.ledgerDue > 0 ? '#b45309' : '#334155', fontFamily: FONT }}>{inr(r.ledgerDue)}</Typography>
            {r.pendingEntries > 0 && <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{r.pendingEntries} entries</Typography>}
          </Box>
        ),
      }),
      col('oldestPendingDays', 'Oldest Unpaid', {
        center: true,
        text: (r) => (r.oldestPendingDays === null ? '' : `${r.oldestPendingDays} days`),
        cell: (r) => {
          if (r.oldestPendingDays === null) return dash;
          const key = r.oldestPendingDays > 30 ? '30+' : r.oldestPendingDays > 15 ? '16-30' : r.oldestPendingDays > 7 ? '8-15' : '0-7';
          const [bg, fg] = AGE_COLORS[key];
          return <Chip size="small" label={`${r.oldestPendingDays}d`} sx={pill(bg, fg)} />;
        },
      }),
      col('mismatch', 'Mismatch', {
        numeric: true,
        text: (r) => r.mismatch,
        print: (r) => (Math.abs(r.mismatch) > 0.5 ? inr(r.mismatch) : '-'),
        extra: (r) => ({ 'Failed Payouts': r.failedPayouts, 'In-flight Payouts': r.inflightPayouts }),
        cell: (r) =>
          Math.abs(r.mismatch) > 0.5 ? (
            <Tooltip title={`Failed payouts ${inr(r.failedPayouts)} · in-flight ${inr(r.inflightPayouts)}. Ledger shows these as settled but the seller hasn't been paid.`} arrow>
              <Chip size="small" icon={<MdWarningAmber />} label={inr(r.mismatch)} sx={{ ...pill('#fee2e2', '#b91c1c'), '& .MuiChip-icon': { color: 'inherit' } }} />
            </Tooltip>
          ) : (
            dash
          ),
      }),
      col('avgSettleDays', 'Avg Settle Time', { center: true, text: (r) => (r.avgSettleDays === null ? '' : `${r.avgSettleDays} days`), cell: (r) => (r.avgSettleDays === null ? dash : `${r.avgSettleDays} d`) }),
      col('lastPayoutAt', 'Last Payout', { text: (r) => (r.lastPayoutAt ? fmtDate(r.lastPayoutAt) : ''), cell: (r) => (r.lastPayoutAt ? fmtDate(r.lastPayoutAt) : dash) }),
      col('bank', 'Payout Account', {
        text: (r) => r.bankLabel || 'Missing',
        cell: (r) => (r.bankReady ? <Typography sx={{ fontSize: 11.5, fontFamily: FONT }}>{r.bankLabel}</Typography> : <Chip size="small" label="Missing" sx={pill('#fee2e2', '#b91c1c')} />),
      }),
    ],
  },
  {
    key: 'ageing',
    label: 'Unpaid Ageing',
    defaultSort: 'days_desc',
    sorts: [
      ['days_desc', 'Oldest first'],
      ['amount_desc', 'Amount: high to low'],
    ],
    columns: [
      col('date', 'Delivered On', { text: (r) => fmtDate(r.date) }),
      col('seller', 'Seller', { text: (r) => r.seller, cell: (r) => <SellerCell r={r} />, extra: (r) => ({ 'Seller Code': r.sellerCode }) }),
      col('orderNumber', 'Order No.', { bold: true, text: (r) => r.orderNumber }),
      col('type', 'Type', { text: (r) => label(r.type), cell: (r) => <Chip size="small" label={r.type === 'return' ? 'Return' : 'Sale'} sx={r.type === 'return' ? pill('#ffedd5', '#9a3412') : pill('#e0e7ff', '#3730a3')} /> }),
      money('amount', 'Amount Due', { bold: true }),
      col('days', 'Days Unpaid', {
        center: true,
        text: (r) => r.days,
        cell: (r) => {
          const [bg, fg] = AGE_COLORS[r.bucket];
          return <Chip size="small" label={`${r.days} days`} sx={pill(bg, fg)} />;
        },
      }),
    ],
  },
  {
    key: 'payouts',
    label: 'Payout History',
    defaultSort: 'date_desc',
    sorts: [
      ['date_desc', 'Newest first'],
      ['date_asc', 'Oldest first'],
      ['amount_desc', 'Amount: high to low'],
    ],
    columns: [
      col('date', 'Initiated', { text: (r) => fmtDate(r.date) }),
      col('seller', 'Seller', { text: (r) => r.seller, cell: (r) => <SellerCell r={r} />, extra: (r) => ({ 'Seller Code': r.sellerCode }) }),
      money('amount', 'Amount', { bold: true }),
      col('paymentMethod', 'Method', { text: (r) => label(r.paymentMethod) }),
      col('gateway', 'Gateway', { text: (r) => label(r.gateway) }),
      col('paidTo', 'Paid To', { text: (r) => r.paidTo }),
      col('reference', 'Reference', { text: (r) => r.reference }),
      col('status', 'Status', {
        text: (r) => label(r.status),
        extra: (r) => ({ 'Gateway Status': r.gatewayStatus, Remarks: r.remarks }),
        cell: (r) => {
          const [bg, fg] = PAYOUT_STATUS[r.status] || ['#f1f5f9', '#475569'];
          return (
            <Tooltip title={r.gatewayStatus ? `Gateway: ${r.gatewayStatus}` : ''} arrow>
              <Chip size="small" label={label(r.status)} sx={pill(bg, fg)} />
            </Tooltip>
          );
        },
      }),
      col('settledAt', 'Settled On', { text: (r) => (r.settledAt ? fmtDate(r.settledAt) : ''), cell: (r) => (r.settledAt ? fmtDate(r.settledAt) : dash) }),
    ],
  },
];

const SellerSettlementReport = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const tab = TABS[tabIndex];
  const [preset, setPreset] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [seller, setSeller] = useState('');
  const [onlyDue, setOnlyDue] = useState('');
  const [ageBucket, setAgeBucket] = useState('');
  const [payoutStatus, setPayoutStatus] = useState('');
  const [sortBy, setSortBy] = useState(TABS[0].defaultSort);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  const filterParams = useMemo(() => {
    const p = { tab: tab.key, sortBy };
    if (from) p.from = from;
    if (to) p.to = to;
    if (search) p.search = search;
    if (seller) p.seller = seller;
    if (onlyDue) p.onlyDue = onlyDue;
    if (tab.key === 'ageing' && ageBucket) p.ageBucket = ageBucket;
    if (tab.key === 'payouts' && payoutStatus) p.payoutStatus = payoutStatus;
    return p;
  }, [tab.key, sortBy, from, to, search, seller, onlyDue, ageBucket, payoutStatus]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, page: page + 1, limit: rowsPerPage }, authToken(), false);
      if (res?.success) setData(res);
      else setError(res?.message || 'Failed to load report');
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, rowsPerPage]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const goToTab = (i, after) => {
    setTabIndex(i);
    setSortBy(TABS[i].defaultSort);
    setPage(0);
    if (after) after();
  };
  const applyPreset = (key) => {
    const range = presetRange(key);
    setPreset(key);
    setFrom(range.from);
    setTo(range.to);
    setPage(0);
  };
  const onDateChange = (setter) => (e) => {
    setter(e.target.value);
    setPreset('');
    setPage(0);
  };
  const setFilter = (setter) => (e) => {
    setter(e.target.value);
    setPage(0);
  };
  const resetFilters = () => {
    setPreset('all');
    setFrom('');
    setTo('');
    setSearchInput('');
    setSearch('');
    setSeller('');
    setOnlyDue('');
    setAgeBucket('');
    setPayoutStatus('');
    setSortBy(tab.defaultSort);
    setPage(0);
  };

  const exportObjects = (rows) =>
    rows.map((r) => {
      const obj = {};
      for (const c of tab.columns) {
        obj[c.label] = c.text(r);
        if (c.extra) Object.assign(obj, c.extra(r));
      }
      return obj;
    });
  const fileStamp = () => `Seller_Settlement_${tab.label.replace(/[^A-Za-z]+/g, '_')}_${from || 'all'}_${to || toISO(new Date())}`;

  const runExport = async (kind) => {
    setExporting(true);
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, export: 'all' }, authToken(), false);
      if (!res?.success) throw new Error(res?.message || 'Export failed');
      const rows = res.rows || [];
      if (kind === 'csv') downloadCsv(exportObjects(rows), fileStamp());
      else if (kind === 'excel') downloadExcel(exportObjects(rows), fileStamp(), tab.label);
      else
        openPrintView({
          title: `Seller Settlement — ${tab.label}`,
          subtitle: `${rangeLabel(from, to)} · ${rows.length} rows`,
          headers: tab.columns.map((c) => c.label),
          cells: rows.map((r) => tab.columns.map((c) => ({ value: c.print ? c.print(r) : c.text(r), className: c.numeric ? 'num' : '' }))),
        });
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const stats = data?.stats || {};
  const total = data?.meta?.total || 0;
  const rows = data?.tab === tab.key ? data.rows || [] : [];
  const sellerOptions = data?.filterOptions?.sellers || [];
  const ageing = stats.ageing || [];
  const ageMax = Math.max(1, ...ageing.map((b) => Math.abs(b.amount)));

  const cards = [
    { label: 'Due to Sellers', value: inr(stats.ledgerDue), icon: MdAccountBalanceWallet, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', sub: `${stats.sellersWithDue || 0} sellers · unsettled ledger`, onClick: () => goToTab(0, () => setOnlyDue('yes')), active: tab.key === 'statement' && onlyDue === 'yes' },
    { label: 'Paid Out', value: inr(stats.paid), icon: MdPayments, gradient: 'linear-gradient(135deg,#10b981,#059669)', sub: `${stats.payoutsCount || 0} completed payouts in period`, onClick: () => goToTab(2, () => setPayoutStatus('completed')), active: tab.key === 'payouts' && payoutStatus === 'completed' },
    { label: 'Overdue > 30 days', value: inr(stats.overdue30), icon: MdHourglassBottom, gradient: 'linear-gradient(135deg,#f43f5e,#dc2626)', sub: 'Unsettled for over a month', onClick: () => goToTab(1, () => setAgeBucket('30+')), active: tab.key === 'ageing' && ageBucket === '30+' },
    { label: 'Failed Payouts', value: inr(stats.failed), icon: MdErrorOutline, gradient: 'linear-gradient(135deg,#be123c,#881337)', sub: `In flight ${inr(stats.inflight)}`, onClick: () => goToTab(2, () => setPayoutStatus('failed')), active: tab.key === 'payouts' && payoutStatus === 'failed' },
    { label: 'Ledger Mismatch', value: inr(stats.mismatchAmount), icon: MdSyncProblem, gradient: 'linear-gradient(135deg,#a855f7,#7e22ce)', sub: `${stats.mismatchSellers || 0} sellers — owed but not showing due`, onClick: () => goToTab(0, () => setSortBy('mismatch_desc')), active: tab.key === 'statement' && sortBy === 'mismatch_desc' },
    { label: 'Missing Bank Details', value: (stats.missingBank || 0).toLocaleString('en-IN'), icon: MdCreditCardOff, gradient: 'linear-gradient(135deg,#64748b,#334155)', sub: 'Sellers with money due' },
    { label: 'Avg Settle Time', value: stats.avgSettleDays === null || stats.avgSettleDays === undefined ? '—' : `${stats.avgSettleDays} days`, icon: MdTimer, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', sub: 'Delivery → settled' },
  ];

  const activeFilterCount = [from || to, search, seller, onlyDue, ageBucket, payoutStatus, sortBy !== tab.defaultSort].filter(Boolean).length;

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 23.5, fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>Seller Settlement Report</Typography>
          <Typography sx={{ fontSize: 12.5, color: '#64748b', fontFamily: FONT }}>What each seller is owed, how long it's been unpaid, and every payout made</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" startIcon={<MdInfoOutline />} onClick={() => setNotesOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', fontFamily: FONT }}>Note</Button>
          <Button startIcon={<MdFileDownload />} disabled={exporting || !total} onClick={() => runExport('csv')} sx={exportButtonSx('#16a34a', '#15803d')}>CSV</Button>
          <Button startIcon={<MdGridOn />} disabled={exporting || !total} onClick={() => runExport('excel')} sx={exportButtonSx('#0d9488', '#0f766e')}>Excel</Button>
          <Button startIcon={<MdPrint />} disabled={exporting || !total} onClick={() => runExport('print')} sx={exportButtonSx('#7c3aed', '#6d28d9')}>Print / PDF</Button>
        </Box>
      </Box>

      <Box sx={statGridSx}>
        {cards.map((c) => (
          <StatCard key={c.label} {...c} />
        ))}
      </Box>

      {stats.mismatchSellers > 0 && (
        <Alert severity="warning" sx={{ mb: 2, fontFamily: FONT }}>
          {stats.mismatchSellers} seller(s) are owed {inr(stats.mismatchAmount)} that no longer shows as due — usually a gateway payout that marked their entries settled and then failed. Review them before the next payout run.
        </Alert>
      )}

      {/* Ageing buckets */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a', mb: 1.5 }}>Unpaid ageing (days since delivery)</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 1.5 }}>
          {ageing.map((b) => {
            const [bg, fg] = AGE_COLORS[b.key];
            const selected = tab.key === 'ageing' && ageBucket === b.key;
            return (
              <Box
                key={b.key}
                onClick={() => goToTab(1, () => setAgeBucket(selected ? '' : b.key))}
                sx={{ p: 1.5, borderRadius: '12px', background: bg, cursor: 'pointer', outline: selected ? `2px solid ${fg}` : 'none', transition: 'transform .15s', '&:hover': { transform: 'translateY(-2px)' } }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: fg, fontFamily: FONT }}>{b.label}</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: fg, fontFamily: FONT }}>{inr(b.amount)}</Typography>
                <Typography sx={{ fontSize: 11.5, color: fg, fontFamily: FONT, opacity: 0.85 }}>{b.entries} entries</Typography>
                <LinearProgress variant="determinate" value={(Math.abs(b.amount) / ageMax) * 100} sx={{ mt: 0.75, height: 5, borderRadius: 3, background: 'rgba(255,255,255,.6)', '& .MuiLinearProgress-bar': { background: fg } }} />
              </Box>
            );
          })}
        </Box>
      </Paper>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>
            Filters {activeFilterCount > 0 && <Chip size="small" label={`${activeFilterCount} active`} color="primary" sx={{ ml: 1, fontWeight: 700 }} />}
          </Typography>
          <Button size="small" startIcon={<MdRefresh />} onClick={resetFilters} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Reset</Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {PRESETS.map((p) => (
            <Chip
              key={p.key}
              label={p.label}
              onClick={() => applyPreset(p.key)}
              sx={{
                fontWeight: 700,
                fontFamily: FONT,
                background: preset === p.key ? 'rgb(255 209 2)' : '#f1f5f9',
                color: '#0f172a',
                border: preset === p.key ? '1px solid #0f172a' : '1px solid transparent',
                '&:hover': { background: preset === p.key ? 'rgb(255 209 2)' : '#e2e8f0' },
              }}
            />
          ))}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField label="From" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={from} onChange={onDateChange(setFrom)} inputProps={{ max: to || undefined }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField label="To" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={to} onChange={onDateChange(setTo)} inputProps={{ min: from || undefined }} />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <TextField
              label="Search seller name / code / phone"
              size="small"
              fullWidth
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><MdSearch size={18} /></InputAdornment>) }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2.5}>
            <TextField select label="Seller" size="small" fullWidth value={seller} onChange={setFilter(setSeller)} SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}>
              <MenuItem value="">All sellers</MenuItem>
              {sellerOptions.map((s) => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2.5}>
            <TextField select label="Show sellers" size="small" fullWidth value={onlyDue} onChange={setFilter(setOnlyDue)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="yes">Only with money due / mismatch</MenuItem>
            </TextField>
          </Grid>
          {tab.key === 'ageing' && (
            <Grid item xs={12} sm={6} md={3} lg={2.5}>
              <TextField select label="Age" size="small" fullWidth value={ageBucket} onChange={setFilter(setAgeBucket)}>
                <MenuItem value="">All</MenuItem>
                {ageing.map((b) => (
                  <MenuItem key={b.key} value={b.key}>{b.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          {tab.key === 'payouts' && (
            <Grid item xs={12} sm={6} md={3} lg={2.5}>
              <TextField select label="Payout status" size="small" fullWidth value={payoutStatus} onChange={setFilter(setPayoutStatus)}>
                <MenuItem value="">All</MenuItem>
                {Object.keys(PAYOUT_STATUS).map((s) => (
                  <MenuItem key={s} value={s}>{label(s)}</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3} lg={2.5}>
            <TextField select label="Sort by" size="small" fullWidth value={sortBy} onChange={setFilter(setSortBy)}>
              {tab.sorts.map(([v, l]) => (
                <MenuItem key={v} value={v}>{l}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => goToTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            borderBottom: '1px solid #e2e8f0',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontFamily: FONT, minHeight: 56 },
            '& .Mui-selected': { color: '#0f172a !important' },
            '& .MuiTabs-indicator': { background: 'rgb(255 209 2)', height: 4, borderRadius: 2 },
          }}
        >
          {TABS.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>{tab.label}</Typography>
          <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>
            {total.toLocaleString('en-IN')} rows{tab.key === 'statement' && ' · Opening + Earned + Returns − Paid Out = Closing'}
          </Typography>
        </Box>
        {(loading || exporting) && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {tab.columns.map((c) => (
                  <TableCell key={c.key} align={c.center ? 'center' : c.numeric ? 'right' : 'left'} sx={{ fontWeight: 800, fontFamily: FONT, background: '#0f172a', color: '#fff', whiteSpace: 'nowrap', py: 1.5 }}>
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={tab.columns.length} sx={{ textAlign: 'center', color: '#94a3b8', py: 6, fontFamily: FONT }}>Nothing matches the selected filters.</TableCell>
                </TableRow>
              )}
              {rows.map((r, idx) => (
                <TableRow
                  key={idx}
                  hover
                  sx={{
                    '&:nth-of-type(even)': { background: '#f8fafc' },
                    boxShadow: (tab.key === 'statement' && Math.abs(r.mismatch) > 0.5) || (tab.key === 'ageing' && r.bucket === '30+') ? 'inset 4px 0 0 #dc2626' : 'none',
                  }}
                >
                  {tab.columns.map((c) => (
                    <TableCell key={c.key} align={c.center ? 'center' : c.numeric ? 'right' : 'left'} sx={{ fontFamily: FONT, whiteSpace: 'nowrap', fontSize: 11.5, fontWeight: c.bold ? 700 : 500, color: '#334155' }}>
                      {c.cell ? c.cell(r) : c.text(r)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={Math.min(page, Math.max(Math.ceil(total / rowsPerPage) - 1, 0))}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        />
      </Paper>

      <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontFamily: FONT }}>Note</DialogTitle>
        <DialogContent dividers>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {(data?.notes || []).map((n, i) => (
              <Typography component="li" key={i} variant="body2" sx={{ mb: 1, fontFamily: FONT }}>{n}</Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SellerSettlementReport;
